import { isInertLink } from "./link-state.js";
import { isDocQuotaBlocked } from "../services/quota-state.js";
/**
 * Node Operations - CRDT-First Mutation API
 *
 * This module provides the primary API for all document node mutations.
 * These functions write directly to CRDT, and the CRDT observer automatically
 * rebuilds the Store. This ensures CRDT is always the source of truth.
 *
 * Extracted from crdt-bridge.js for modularity.
 *
 * Usage:
 *   import { addNode, updateNode, removeNode, moveNode } from './node-operations.js';
 *   addNode(parentId, { name: 'New Node' });
 *   updateNode(nodeId, { status: 'done' });
 *   removeNode(nodeId);
 *   moveNode(nodeId, newParentId, index);
 */

import { emit, Events } from "../core/events.js";
import { t } from "../../shared/i18n/index.js";
import { createLogger } from "../core/logger.js";
import { generateUUID } from "../core/uuid.js";
import { transferClipboardTags } from "../core/clipboard-tags.js";
import { isActiveShareReadOnly, isActiveShareDeleteBlocked, activeReadOnlyReason, getActiveShareId, getShareRole } from "./share-state.js";
import { isMapFull } from "../services/map-size.js";
import { isLinkedOffline } from "./linked-offline.js";
import {
  DIMENSION_FIELDS,
  storeLocalDimensions,
  clearLocalDimensionsForNode as _clearLocalDimensionsForNode,
} from "./dimension-manager.js";
import { getSharedDocManager } from "./share-lifecycle.js";
import { USER_ORIGIN, LAYOUT_ORIGIN } from "./origins.js";
import { LOCAL_FIELDS } from "./map-format.js";
import * as StorageHealth from "./storage-health.js";

// Fields that remain anchored on the stub in its host doc (rendering
// position and local layout for the attachment point). All other fields
// edited on a stub write to the linked doc's root instead. Exported so
// batch-operations can apply the same split during flushing.
export const STUB_LAYOUT_FIELDS = new Set([
  "x", "y",
  "mmX", "mmY", "mmSide",
  "mmOffsetX", "mmOffsetY",
  "mmWidth", "mmHeight", "mmTextHash",
  "collapsed",
]);

// ============================================
// Logging Setup
// ============================================
const log = createLogger("Node Operations");

const logError = (...args) => log.error(...args);
const logWarn = (...args) => log.warn(...args);
const logInfo = (...args) => log.info(...args);
const logDebug = (...args) => log.debug(...args);

// ============================================
// Callback Injection Pattern
// ============================================

/**
 * Callbacks provided by crdt-bridge.js during initialization
 * @type {Object}
 */
let callbacks = {
  getBatchBuffer: null,        // () => batchBuffer (null or array)
  getDocManager: null,         // () => docManager (main doc manager)
  getStore: null,              // () => store module
  getDocManagerForLinkedNode: null, // (nodeId) => { docManager, linkedDocId }
};

/**
 * Initialize node operations with callbacks from crdt-bridge.js
 * @param {Object} cbs - Callback functions
 */
export function initNodeOperations(cbs) {
  callbacks = { ...callbacks, ...cbs };
}

/**
 * Check if node operations are initialized
 * @returns {boolean} True if initialized
 */
function isInitialized() {
  return callbacks.getStore !== null;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Generate a unique node ID using UUID format
 * @returns {string} A unique node ID in UUID format
 *
 * This is the canonical ID generation function for CRDT-first mutations.
 */
export function generateNodeId() {
  return generateUUID();
}

/**
 * Get the appropriate doc manager(s) for a node.
 *
 * For any node, `hostManager` owns its Y.Map entry and is the target for
 * removal, moving, and layout field writes. `docManager` is the content
 * target — same as hostManager for regular nodes; for stubs it's the linked
 * doc the stub points to (with `translatedId` as the id to use there).
 *
 * @param {string} nodeId
 * @returns {{
 *   docManager, hostManager, shareId, linkedDocId, hostDocId, translatedId, isStub,
 * }}
 */
function getDocManagerForNode(nodeId) {
  const activeShare = getActiveShareId();
  if (activeShare) {
    const shareMgr = getSharedDocManager(activeShare);
    return {
      docManager: shareMgr, hostManager: shareMgr,
      shareId: activeShare, linkedDocId: null, hostDocId: activeShare,
      translatedId: nodeId, isStub: false,
    };
  }

  const getLinkedDM = callbacks.getDocManagerForLinkedNode;
  const linkedResult = getLinkedDM ? getLinkedDM(nodeId) : null;
  if (linkedResult && (linkedResult.linkedDocId || linkedResult.hostDocId)) {
    return {
      docManager: linkedResult.docManager,
      hostManager: linkedResult.hostManager,
      shareId: null,
      linkedDocId: linkedResult.linkedDocId,
      hostDocId: linkedResult.hostDocId,
      translatedId: linkedResult.translatedId || nodeId,
      isStub: !!linkedResult.isStub,
    };
  }

  const dm = callbacks.getDocManager();
  return {
    docManager: dm, hostManager: dm,
    shareId: null, linkedDocId: null, hostDocId: null,
    translatedId: nodeId, isStub: false,
  };
}

/** Human-readable target label for logs. */
function targetLabel({ shareId, linkedDocId, isStub }) {
  if (shareId) return `(shared: ${shareId})`;
  if (linkedDocId) return isStub ? `(stub → linked: ${linkedDocId})` : `(linked: ${linkedDocId})`;
  return "(main doc)";
}

/**
 * True when a write routed to `linkedDocId` would be rejected by the server
 * because the user only has `viewer` role on that doc. We must block these
 * writes locally, otherwise the Y.Doc applies the change optimistically and
 * the user sees a ghost edit that disappears on server rejection. Applies
 * to writes that target a linked doc; writes to a doc the user is actively
 * viewing as a share are already gated by `isActiveShareReadOnly`.
 * @param {string|null} linkedDocId
 */
function isLinkedDocReadOnly(linkedDocId) {
  return isLinkedDocDeleteBlocked(linkedDocId) || isMapFull(linkedDocId);
}

/** Read-only in a way that also stops deleting (a full map allows it). */
function isLinkedDocDeleteBlocked(linkedDocId) {
  if (!linkedDocId) return false;
  // Offline linked maps are shown from cache until the server confirms access.
  return getShareRole(linkedDocId) === "viewer" || isDocQuotaBlocked(linkedDocId) || isLinkedOffline(linkedDocId);
}

/**
 * Emit EDIT_BLOCKED (triggers the global toast) for a write rejected on a
 * linked doc (viewer role, offline, quota, or the map is full), and return false.
 */
function blockLinkedViewerWrite(action, linkedDocId) {
  emit(Events.EDIT_BLOCKED, {
    action,
    reason: t(isLinkedDocDeleteBlocked(linkedDocId) ? "sync.edit.linkedUnavailable" : "sync.edit.mapFull"),
  });
  return false;
}

/** The message for an edit refused on the open map. */
function readOnlyReason() {
  return t(activeReadOnlyReason() === "mapFull" ? "sync.edit.mapFull" : "sync.edit.readOnly");
}

/**
 * A link shown as a badge (nested link, duplicate, conflict, no access,
 * still loading, offline copy) has no expanded content here. Anything typed
 * "into" it would be written under the link node in its host map and stay
 * hidden behind the linked content: only moving, deleting and layout are
 * allowed on it.
 * @param {string} nodeId
 */
function isInertLinkBadge(nodeId) {
  return isInertLink(callbacks.getStore()?.find?.(nodeId));
}

function blockInertBadgeWrite(action) {
  emit(Events.EDIT_BLOCKED, {
    action,
    reason: t("sync.edit.linkedNotShown"),
  });
  return false;
}

/**
 * Wrapper for clearLocalDimensionsForNode that injects store.find
 * This avoids circular dependency in dimension-manager.js
 * @param {string} nodeId - Node ID
 */
function clearLocalDimensionsForNode(nodeId) {
  const store = callbacks.getStore();
  if (store) {
    _clearLocalDimensionsForNode(nodeId, store.find);
  }
}

// ============================================
// CRDT-First Public Mutation API
// ============================================

/**
 * Add a node to the document (CRDT-first)
 *
 * Writes directly to CRDT. The Store is automatically rebuilt via observer.
 *
 * @param {string} parentId - Parent node ID
 * @param {Object} nodeData - Node data (id optional, will be generated)
 * @param {number} [index=-1] - Position in parent's children (-1 = end)
 * @returns {Object|null} Created node or null if blocked
 */
export function addNode(parentId, nodeData, index = -1) {
  // Guard: Check if node operations are initialized
  // This can happen when opening a shared map as first action (before any personal map)
  if (!isInitialized()) {
    logError("addNode: Node operations not initialized");
    return null;
  }
  StorageHealth.assertHealthy();

  // Prevent edits if user has view-only access
  if (isActiveShareReadOnly()) {
    logWarn("Cannot add node: read-only share");
    emit(Events.EDIT_BLOCKED, {
      action: "add",
      reason: readOnlyReason(),
    });
    return null;
  }

  if (isInertLinkBadge(parentId)) {
    logWarn("addNode blocked: parent is a link badge", parentId);
    blockInertBadgeWrite("add");
    return null;
  }

  // Generate ID if not provided
  const node = {
    id: nodeData.id || generateNodeId(),
    name: nodeData.name || t("sync.node.defaultName"),
    children: nodeData.children || [],
    ...nodeData,
    createdAt: nodeData.createdAt || Date.now(),
  };

  // Ensure children is always an array
  if (!Array.isArray(node.children)) {
    node.children = [];
  }

  // Route to correct doc manager. If parent is a stub, children belong to the
  // linked doc and the parent id must be translated to the linked root id.
  const { docManager: targetDocManager, shareId, linkedDocId, translatedId: writeParentId, isStub } =
    getDocManagerForNode(parentId);

  // Block writes to a linked doc when the user only has viewer access.
  // Done before any batch buffering so bulk flows (paste/import) are also
  // caught, and before any Y.Doc write so the UI doesn't optimistically show
  // a change that the server will reject.
  if (isLinkedDocReadOnly(linkedDocId)) {
    logWarn("addNode blocked: viewer access on linked doc", linkedDocId);
    blockLinkedViewerWrite("add", linkedDocId);
    return null;
  }

  // If batching, buffer the operation instead of immediate CRDT write
  const batchBuffer = callbacks.getBatchBuffer();
  if (batchBuffer !== null) {
    batchBuffer.push({ type: 'add', parentId, node, index });
    return node; // Return node immediately (will be written in endBatch)
  }

  if (!targetDocManager) {
    // Parent is tracked as living in a linked doc, but that linked doc's
    // manager isn't initialized — either the user has no access, or
    // ensureLinkedDocSync hasn't completed yet for this session. Surface
    // it as an EDIT_BLOCKED toast instead of silently dropping the input,
    // so the user knows their keypress didn't take effect.
    const reason = linkedDocId
      ? t("sync.edit.linkedLoading")
      : t("sync.edit.noRoute");
    logWarn("addNode: No doc manager for parent:", parentId, "linkedDocId:", linkedDocId);
    emit(Events.EDIT_BLOCKED, { action: "add", reason });
    return null;
  }

  logInfo(
    "addNode (CRDT-first):",
    node.id,
    "to parent:",
    writeParentId,
    targetLabel({ shareId, linkedDocId, isStub })
  );

  // Write to CRDT - observer will rebuild Store
  targetDocManager.addNode(node, writeParentId, index);

  // Emit event for compatibility (analytics, history, etc.)
  emit(Events.NODE_ADDED, { parentId, node, index });

  return node;
}

/** Paste a subtree and its tag definitions in one routed transaction. */
export function pasteNode(parentId, node, tagDefinitions = []) {
  StorageHealth.assertHealthy();
  if (callbacks.getBatchBuffer?.() != null) {
    throw new Error('Cannot paste while another batch is in progress');
  }
  const { docManager: target, translatedId } = getDocManagerForNode(parentId);
  if (!target?.hasNode(translatedId)) {
    emit(Events.EDIT_BLOCKED, { action: 'paste', reason: t('sync.edit.destinationLoading') });
    return null;
  }
  const existing = target.getTagDefinitions();
  const prepared = transferClipboardTags(node, () => tagDefinitions, existing);
  let added = null;
  target.doc.transact(() => {
    // Keep addNode's permission/quota checks and linked-parent translation.
    added = addNode(parentId, prepared.node);
    if (added && prepared.tagDefinitions.length > existing.length) {
      target.setTagDefinitions(prepared.tagDefinitions);
    }
  }, USER_ORIGIN);
  return added;
}

/**
 * Fields a map in schema v2 keeps on this device (folded, measured sizes:
 * engine/sync/local-node-state.js) change nothing in the CRDT, so no observer
 * rebuilds the Store: show them directly, as for a shared map's sizes.
 */
function showKeptOnDevice(store, nodeId, kept) {
  if (!kept || Object.keys(kept).length === 0) return;
  // A fold shows (the views redraw); sizes alone are layout caching.
  store._updateNodeDirect(nodeId, kept, { dimensionOnly: Object.keys(kept).every((key) => DIMENSION_FIELDS.has(key)) });
}

/**
 * Update a node in the document (CRDT-first)
 *
 * Writes directly to CRDT. The Store is automatically rebuilt via observer.
 *
 * @param {string} nodeId - Node ID to update
 * @param {Object} updates - Properties to update
 * @returns {boolean} True if update was applied, false if blocked
 */
export function updateNode(nodeId, updates) {
  // Guard: Check if node operations are initialized
  // This can happen when opening a shared map as first action (before any personal map)
  if (!isInitialized()) {
    logError("updateNode: Node operations not initialized");
    return false;
  }
  StorageHealth.assertHealthy();

  const store = callbacks.getStore();

  // Phase 2.1 (PLAN17): Check if this is a dimension-only update
  const isDimensionOnly = Object.keys(updates).every(key => DIMENSION_FIELDS.has(key));

  // A map in schema v2 keeps folding and sizes on this device: nothing to
  // send, so any role may do it (a viewer folds a branch too).
  if (Object.keys(updates).length > 0 && Object.keys(updates).every(key => LOCAL_FIELDS.has(key))) {
    const routing = getDocManagerForNode(nodeId);
    const owner = routing.isStub ? routing.hostManager : routing.docManager;
    if (owner?.isV2?.()) {
      showKeptOnDevice(store, nodeId, owner.updateNode(routing.isStub ? nodeId : routing.translatedId, updates, LAYOUT_ORIGIN));
      return true;
    }
  }

  // Prevent edits if user has view-only access
  // Phase 2.1 (PLAN17): Allow dimension-only updates even in read-only mode
  // (dimensions are local layout data, not content edits)
  if (isActiveShareReadOnly() && !isDimensionOnly) {
    logWarn("Cannot update node: read-only share");
    emit(Events.EDIT_BLOCKED, {
      action: "update",
      reason: readOnlyReason(),
    });
    return false;
  }

  // Phase 2.1 (PLAN17): For read-only mode with dimension-only updates,
  // store locally and return early (no CRDT sync)
  if (isActiveShareReadOnly() && isDimensionOnly) {
    storeLocalDimensions(nodeId, updates);
    store._updateNodeDirect(nodeId, updates);
    return true;
  }

  if (!isDimensionOnly && isInertLinkBadge(nodeId)
      && !Object.keys(updates).every(k => STUB_LAYOUT_FIELDS.has(k))) {
    logWarn("updateNode blocked: content edit on a link badge", nodeId);
    return blockInertBadgeWrite("update");
  }

  // Viewer-role check for linked-doc writes. Must run before batching so
  // bulk flows are caught, and before any Y.Doc write.
  //
  // Dimension-only writes (mmWidth/mmHeight/mmTextHash) are layout caching
  // that never sync to the CRDT for linked docs anyway — store them
  // locally and return success regardless of role. Mirrors the read-only
  // shared-doc path.
  {
    const routing = getDocManagerForNode(nodeId);
    if (isLinkedDocReadOnly(routing.linkedDocId)) {
      if (isDimensionOnly) {
        storeLocalDimensions(nodeId, updates);
        store._updateNodeDirect(nodeId, updates);
        return true;
      }
      if (!routing.isStub) {
        logWarn("updateNode blocked: viewer access on linked doc", routing.linkedDocId);
        return blockLinkedViewerWrite("update", routing.linkedDocId);
      }
      const isLayoutOnly = Object.keys(updates).every(k => STUB_LAYOUT_FIELDS.has(k));
      if (!isLayoutOnly) {
        logWarn("updateNode blocked: viewer access on linked doc (content)", routing.linkedDocId);
        return blockLinkedViewerWrite("update", routing.linkedDocId);
      }
    }
  }

  // If batching, buffer the operation instead of immediate CRDT write
  const batchBuffer = callbacks.getBatchBuffer();
  if (batchBuffer !== null) {
    // M1 Fix (PLAN18): For shared nodes, split dimension fields before buffering
    // Dimension fields should be stored locally, not synced to CRDT
    const { shareId } = getDocManagerForNode(nodeId);
    if (shareId) {
      const dimensionUpdates = {};
      const syncableUpdates = {};

      for (const [key, value] of Object.entries(updates)) {
        if (DIMENSION_FIELDS.has(key)) {
          dimensionUpdates[key] = value;
        } else {
          syncableUpdates[key] = value;
        }
      }

      // Apply dimension fields locally immediately (not buffered)
      if (Object.keys(dimensionUpdates).length > 0) {
        storeLocalDimensions(nodeId, dimensionUpdates);
        store._updateNodeDirect(nodeId, dimensionUpdates);
      }

      // If only dimension updates, we're done
      if (Object.keys(syncableUpdates).length === 0) {
        return true;
      }

      // Buffer only non-dimension fields for CRDT
      updates = syncableUpdates;
    }

    // F4.2: Capture oldValues for batched updates
    const oldValues = {};
    const storeNode = store.find(nodeId);
    if (storeNode) {
      for (const key of Object.keys(updates)) {
        if (storeNode[key] !== undefined) {
          oldValues[key] = storeNode[key];
        }
      }
    }
    batchBuffer.push({ type: 'update', nodeId, updates, oldValues, shareId });
    return true;
  }

  // Route to correct doc manager. Viewer-role was checked earlier.
  const { docManager: targetDocManager, shareId, linkedDocId, translatedId, isStub } =
    getDocManagerForNode(nodeId);

  if (!targetDocManager) {
    const reason = linkedDocId
      ? t("sync.edit.linkedLoading")
      : t("sync.edit.noRoute");
    logWarn("updateNode: No doc manager for node:", nodeId, "linkedDocId:", linkedDocId);
    emit(Events.EDIT_BLOCKED, { action: "update", reason });
    return false;
  }

  // For stubs, layout fields belong to the stub in its host doc (the doc
  // that owns the stub — main for top-level, an ancestor linked doc for
  // nested stubs), while content fields belong to the linked root in the
  // doc the stub points to. Split the update and write to each accordingly.
  if (isStub) {
    const { hostManager } = getDocManagerForNode(nodeId);
    const layoutUpdates = {};
    let contentUpdates = {};
    for (const [k, v] of Object.entries(updates)) {
      if (STUB_LAYOUT_FIELDS.has(k)) layoutUpdates[k] = v;
      else contentUpdates[k] = v;
    }
    if (hostManager && Object.keys(layoutUpdates).length > 0) {
      const writeOrigin = Object.keys(layoutUpdates).every(k => DIMENSION_FIELDS.has(k))
        ? LAYOUT_ORIGIN : USER_ORIGIN;
      showKeptOnDevice(store, nodeId, hostManager.updateNode(nodeId, layoutUpdates, writeOrigin));
    }
    // Notes intercept for linked doc (yxml-v1 stores notes as Y.XmlFragment)
    if ('notes' in contentUpdates && typeof contentUpdates.notes === 'string'
        && targetDocManager.metadata?.get('notesFormat') === 'yxml-v1') {
      targetDocManager.setNodeNotesFromMarkdown(translatedId, contentUpdates.notes);
      const { notes: _removed, ...rest } = contentUpdates;
      contentUpdates = rest;
    }
    if (Object.keys(contentUpdates).length > 0) {
      targetDocManager.updateNode(translatedId, contentUpdates, USER_ORIGIN);
    }
    emit(Events.NODE_UPDATED, { nodeId, updates, oldValues: {} });
    return true;
  }

  // F4.3 / linked-doc extension: store dimension fields LOCALLY for any doc
  // other than the user's own main. Rationale: mmWidth/mmHeight/mmTextHash
  // caches depend on the viewer's rendering (fonts, screen size, zoom) and
  // must not stomp another member's cached values. Applies to:
  //   - shared docs viewed as a share (shareId present)
  //   - nodes inside a composited linked-doc subtree (linkedDocId set,
  //     isStub false — the node itself lives in the linked doc)
  // Stubs still write layout to their host (main by default), which is fine
  // because stub-layout isn't shared with anyone viewing the linked doc
  // directly.
  if (shareId || (linkedDocId && !isStub)) {
    const dimensionUpdates = {};
    const syncableUpdates = {};

    for (const [key, value] of Object.entries(updates)) {
      if (DIMENSION_FIELDS.has(key)) {
        dimensionUpdates[key] = value;
      } else {
        syncableUpdates[key] = value;
      }
    }

    if (Object.keys(dimensionUpdates).length > 0) {
      storeLocalDimensions(nodeId, dimensionUpdates);
      // Phase 12 Fix: also update Store directly so layout is visible
      // immediately (dimensions are local-only, no CRDT observer to trigger).
      store._updateNodeDirect(nodeId, dimensionUpdates);
    }

    if (Object.keys(syncableUpdates).length === 0) {
      return true;
    }

    updates = syncableUpdates;
  }

  // F4.2: Capture oldValues before CRDT write for event emission
  const oldValues = {};
  const storeNode = store.find(nodeId);
  if (storeNode) {
    for (const key of Object.keys(updates)) {
      if (storeNode[key] !== undefined) {
        oldValues[key] = storeNode[key];
      }
    }
  }

  logInfo(
    "updateNode (CRDT-first):",
    nodeId,
    Object.keys(updates),
    targetLabel({ shareId, linkedDocId, isStub })
  );

  // An item not in this map (gone, or of another map opened meanwhile): a
  // change for it would be said "saved" and kept nowhere (its note in a
  // fragment nothing shows).
  if (targetDocManager.hasNode && !targetDocManager.hasNode(nodeId)) {
    logWarn('updateNode: an item not in the map, refused:', nodeId);
    return false;
  }

  // Intercept notes updates: redirect string notes to Y.XmlFragment
  // (when notesFormat is 'yxml-v1', notes live in fragments, not flat nodes)
  if ('notes' in updates && typeof updates.notes === 'string' && targetDocManager.metadata?.get('notesFormat') === 'yxml-v1') {
    targetDocManager.setNodeNotesFromMarkdown(nodeId, updates.notes);
    const { notes: _removed, ...rest } = updates;
    updates = rest;
    if (Object.keys(updates).length === 0) {
      emit(Events.NODE_UPDATED, { nodeId, updates: { notes: _removed }, oldValues });
      return true; // Only notes changed, fragment handles it
    }
  }

  // Write to CRDT - observer will rebuild Store.
  // Pure dimension-only writes are layout caching and must NOT enter the
  // undo history (we don't want Ctrl+Z to revert a text-size recalculation).
  // Any update that touches a content field rides along as USER_ORIGIN.
  const writeOrigin = Object.keys(updates).every(k => DIMENSION_FIELDS.has(k))
    ? LAYOUT_ORIGIN
    : USER_ORIGIN;
  showKeptOnDevice(store, nodeId, targetDocManager.updateNode(nodeId, updates, writeOrigin));

  // Emit event for compatibility (analytics, history, etc.)
  emit(Events.NODE_UPDATED, { nodeId, updates, oldValues });

  return true;
}

/**
 * Remove a node from the document (CRDT-first)
 *
 * Writes directly to CRDT. The Store is automatically rebuilt via observer.
 * Handles focus/selection cleanup before removal.
 *
 * @param {string} nodeId - Node ID to remove
 * @returns {boolean} True if removal was applied, false if blocked
 */
export function removeNode(nodeId) {
  // Guard: Check if node operations are initialized
  // This can happen when opening a shared map as first action (before any personal map)
  if (!isInitialized()) {
    logError("removeNode: Node operations not initialized");
    return false;
  }
  StorageHealth.assertHealthy();

  const store = callbacks.getStore();

  // Prevent edits if user has view-only access. A full map allows deleting:
  // it is how the user makes room (engine/services/map-size.js).
  if (isActiveShareDeleteBlocked()) {
    logWarn("Cannot remove node: read-only share");
    emit(Events.EDIT_BLOCKED, {
      action: "delete",
      reason: t("sync.edit.readOnly"),
    });
    return false;
  }

  // Focus and selection are the interface's: when the tree no longer has
  // the focused item, the Store moves the focus to the closest of its
  // parents still there, and clears a selection gone (interfaces/classic/store/index.js).

  // Get parent and node info before removal (needed for events)
  const node = store.find(nodeId);
  const parent = store.findParent(nodeId);
  const parentId = parent?.id || null;

  // Resolve routing and check viewer-role before any state change so the
  // guard applies whether or not we end up in the batch buffer path.
  const routing = getDocManagerForNode(nodeId);
  const removeTargetDocId = routing.isStub ? routing.hostDocId : routing.linkedDocId;
  if (isLinkedDocDeleteBlocked(removeTargetDocId)) {
    logWarn("removeNode blocked: viewer access on linked doc", removeTargetDocId);
    return blockLinkedViewerWrite("delete", removeTargetDocId);
  }

  // HI5 Fix: Clear local dimensions for removed node and descendants
  // Must be done BEFORE removal so we can traverse children
  clearLocalDimensionsForNode(nodeId);

  // If batching, buffer the operation instead of immediate CRDT write
  const batchBuffer = callbacks.getBatchBuffer();
  if (batchBuffer !== null) {
    batchBuffer.push({ type: 'remove', nodeId, parentId, node });
    return true;
  }

  const { docManager: contentMgr, hostManager, shareId, linkedDocId, isStub } = routing;
  const targetDocManager = isStub ? hostManager : contentMgr;

  if (!targetDocManager) {
    const reason = linkedDocId
      ? t("sync.edit.linkedLoading")
      : t("sync.edit.noRoute");
    logWarn("removeNode: No doc manager for node:", nodeId, "linkedDocId:", linkedDocId);
    emit(Events.EDIT_BLOCKED, { action: "remove", reason });
    return false;
  }

  logInfo(
    "removeNode (CRDT-first):",
    nodeId,
    isStub ? "(unlink — host doc)" : targetLabel({ shareId, linkedDocId: routing.linkedDocId, isStub: false })
  );

  // Write to CRDT - observer will rebuild Store
  targetDocManager.removeNode(nodeId);

  // Emit event for compatibility (analytics, history, etc.)
  emit(Events.NODE_REMOVED, { nodeId, parentId, node });

  return true;
}

/**
 * Move a node to a new parent (CRDT-first)
 *
 * Writes directly to CRDT. The Store is automatically rebuilt via observer.
 * Handles cross-boundary moves (between main doc and shared docs).
 *
 * @param {string} nodeId - Node ID to move
 * @param {string} newParentId - New parent node ID
 * @param {number} [index=-1] - Position in new parent's children (-1 = end)
 * @returns {boolean} True if move was applied, false if blocked
 */
export function moveNode(nodeId, newParentId, index = -1) {
  // Guard: Check if node operations are initialized
  // This can happen when opening a shared map as first action (before any personal map)
  if (!isInitialized()) {
    logError("moveNode: Node operations not initialized");
    return false;
  }
  StorageHealth.assertHealthy();

  const store = callbacks.getStore();
  const docManager = callbacks.getDocManager();

  // Prevent edits if user has view-only access
  if (isActiveShareReadOnly()) {
    logWarn("Cannot move node: read-only share");
    emit(Events.EDIT_BLOCKED, {
      action: "move",
      reason: readOnlyReason(),
    });
    return false;
  }

  if (isInertLinkBadge(newParentId)) {
    logWarn("moveNode blocked: new parent is a link badge", newParentId);
    return blockInertBadgeWrite("move");
  }

  // Get old parent for event payload
  const oldParent = store.findParent(nodeId);
  const oldParentId = oldParent?.id || null;

  // Resolve source/target docs. Cross-doc moves are blocked — the user must
  // copy-paste to carry content across documents (prevents accidentally
  // absorbing shared/linked-doc subtrees into a personal map).
  const src = getDocManagerForNode(nodeId);
  const dst = getDocManagerForNode(newParentId);
  // The doc a node actually "sits in" for the purpose of reparenting:
  //   - source: always the doc that owns the moving node's entry (host).
  //   - target: if the user dropped onto a stub, the new child would land in
  //     the linked doc the stub points to; otherwise in the parent's host.
  const srcDoc = src.shareId || src.hostDocId || "main";
  const dstDoc = dst.shareId || (dst.isStub ? dst.linkedDocId : dst.hostDocId) || "main";
  if (srcDoc !== dstDoc) {
    logWarn("moveNode: cross-doc move blocked", nodeId, "->", newParentId);
    emit(Events.EDIT_BLOCKED, {
      action: "move",
      reason: t("sync.edit.crossDocMove"),
    });
    return false;
  }

  // Moves inside a linked doc require write access to that doc. Checked
  // before batching so bulk flows hit the same guard.
  const moveTargetDocId = src.isStub ? src.hostDocId : src.linkedDocId;
  if (isLinkedDocReadOnly(moveTargetDocId)) {
    logWarn("moveNode blocked: viewer access on linked doc", moveTargetDocId);
    return blockLinkedViewerWrite("move", moveTargetDocId);
  }

  // If batching, buffer the operation instead of immediate CRDT write
  // For moves, we store the node data in case it's a cross-boundary move
  const batchBuffer = callbacks.getBatchBuffer();
  if (batchBuffer !== null) {
    const node = store.find(nodeId);
    batchBuffer.push({ type: 'move', nodeId, newParentId, index, oldParentId, node });
    return true;
  }

  // A stub's move happens in its HOST doc. For non-stubs, host === content.
  // For the parent: if dropped onto a stub, write using the linked root's id
  // (the parent in the linked doc); otherwise the parent's id as-is.
  const targetDocManager = src.isStub ? src.hostManager : src.docManager;
  const writeNodeId = src.isStub ? nodeId : src.translatedId;
  const writeParentId = dst.translatedId;
  const shareId = src.shareId;

  if (!targetDocManager) {
    const linkedDocId = src.linkedDocId || src.hostDocId || null;
    const reason = linkedDocId
      ? t("sync.edit.linkedLoading")
      : t("sync.edit.noRoute");
    logWarn("moveNode: No doc manager for node:", nodeId, "linkedDocId:", linkedDocId);
    emit(Events.EDIT_BLOCKED, { action: "move", reason });
    return false;
  }

  logInfo(
    "moveNode (CRDT-first):",
    writeNodeId,
    "to:",
    writeParentId,
    "at index:",
    index,
    src.isStub
      ? "(stub move — host doc)"
      : targetLabel({ shareId, linkedDocId: src.linkedDocId, isStub: false })
  );

  // Write to CRDT - observer will rebuild Store
  targetDocManager.moveNode(writeNodeId, writeParentId, index);

  // Emit event for compatibility
  emit(Events.NODE_MOVED, { nodeId, oldParentId, newParentId, index });

  return true;
}
